import { Typography, type TypographyProps } from '@mui/material'
import React, { useMemo } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { NODE_TEXT_MAX_WIDTH } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import {
  getExternalActionTargetPrefix,
  getExternalActionText,
  getExternalRollbackActionText,
  getResourceNameFromExternalActionRequest
} from 'components/scenarios/utils/graph/messaging'
import {
  isExternalActionNode,
  isRollbackExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import {
  type ExternalActionNode,
  ExternalActionRequestType,
  type RollbackExternalActionNode
} from 'types/api/scenarios/scenario'

const IMPACTING_EXTERNAL_ACTION_REQUEST_TYPES = new Set([
  ExternalActionRequestType.DisableAWSAutoScalingGroups,
  ExternalActionRequestType.RemoveAWSSecurityGroups,
  ExternalActionRequestType.RunBlazeMeterTest
])

const TYPOGRAPHY_SPAN_PROPS = { component: 'span', variant: 'inherit' } as const

export const ExternalActionDescription = ({
  node,
  shouldDisplayResourceName = true,
  ...typographyWrapperProps
}: {
  node: ExternalActionNode | RollbackExternalActionNode
  shouldDisplayResourceName?: boolean
} & Omit<TypographyProps, 'children'>) => {
  const { externalActionRequest } = node

  const isNoOpExternalActionRequest =
    externalActionRequest.type === ExternalActionRequestType.NoOp

  const { action, actionColor, target, resourceName } = useMemo(() => {
    const externalActionRequestType = isNoOpExternalActionRequest
      ? externalActionRequest.substitutedRequestType
      : externalActionRequest.type

    const isImpactingAction =
      isExternalActionNode(node) &&
      IMPACTING_EXTERNAL_ACTION_REQUEST_TYPES.has(externalActionRequestType)

    const actionColor = isImpactingAction ? 'error' : 'success.main'

    const resourceName = isNoOpExternalActionRequest
      ? null
      : getResourceNameFromExternalActionRequest(externalActionRequest)

    return {
      action: isRollbackExternalActionNode(node)
        ? getExternalRollbackActionText(externalActionRequestType)
        : getExternalActionText(externalActionRequestType),
      actionColor,
      target: getExternalActionTargetPrefix(
        externalActionRequestType,
        shouldDisplayResourceName && !isNoOpExternalActionRequest
      ),
      resourceName
    }
  }, [
    externalActionRequest,
    isNoOpExternalActionRequest,
    node,
    shouldDisplayResourceName
  ])

  return (
    <Typography
      noWrap
      sx={{
        maxWidth: NODE_TEXT_MAX_WIDTH
      }}
      variant="body1"
      {...typographyWrapperProps}
    >
      <Typography
        color={actionColor}
        fontWeight="bold"
        {...TYPOGRAPHY_SPAN_PROPS}
      >
        {action}
      </Typography>{' '}
      <Typography {...TYPOGRAPHY_SPAN_PROPS}>
        {target}{' '}
        {shouldDisplayResourceName && !isNoOpExternalActionRequest && (
          <TruncatedTextWithTooltip
            fontWeight="bold"
            tooltipProps={{
              title: resourceName
            }}
            {...TYPOGRAPHY_SPAN_PROPS}
          >
            {resourceName}
          </TruncatedTextWithTooltip>
        )}
      </Typography>
    </Typography>
  )
}
